/**
 * A frase que a API mandou, quando ela mandou alguma que sirva para a pessoa.
 *
 * A API responde erro em três formas, e quem lia só uma delas mostrava um texto
 * genérico no lugar do motivo:
 *
 * 1. `{ mensagem: "..." }`, das nossas exceções (o padrão do projeto).
 * 2. `{ message: "..." }`, das exceções prontas do Nest (`BadRequestException`),
 *    que é como sai "Este email já está em uso por outro usuário."
 * 3. `{ message: ["...", "..."] }`, da validação de DTO, uma frase por campo.
 *
 * Editar um aluno com um e-mail já usado caía no caso 2: o servidor dizia
 * exatamente o que estava errado, o app lia só `mensagem`, e a tela mostrava
 * "Erro ao atualizar aluno." A mesma mensagem específica já aparecia no
 * cadastro, o que fazia parecer defeito só da edição.
 *
 * E nem toda frase que chega foi escrita para gente. O `message` do `ApiError`
 * sai daqui, e é ele que as telas põem no aviso (`err?.message || "..."`), então
 * dois tipos de texto ficam de fora e viram o `padrao`:
 *
 * - o que o Nest, o Express e o class-validator escrevem sozinhos, em inglês: o
 *   guard recusa com `{ message: "Unauthorized" }`, a rota que não existe com
 *   "Cannot POST /x", e o DTO sem mensagem própria com "duracao_segundos must be
 *   an integer number";
 * - o que tem cara de erro interno: pilha, caminho de arquivo, classe de erro,
 *   SQL, código de rede do Node. A API não manda isso (o erro que ela não conhece
 *   sai como "Internal server error", e o detalhe fica no log do servidor), e o
 *   filtro garante que, se uma rota um dia mandar, o texto pare aqui.
 *
 * O `data` do `ApiError` continua com a resposta inteira: quem decide pelo texto
 * cru (a corrida que sumiu, o Tabata do servidor antigo) lê `err.data.message`.
 */
export function mensagemDaApi(dados: unknown, padrao: string): string {
  if (!dados || typeof dados !== "object") return padrao
  const d = dados as { mensagem?: unknown; message?: unknown; erro?: unknown }

  const primeiraFrase = (v: unknown): string | null => {
    const candidatas = Array.isArray(v) ? v : [v]
    for (const x of candidatas) {
      if (typeof x === "string" && fraseParaGente(x.trim())) return x.trim()
    }
    return null
  }

  return primeiraFrase(d.mensagem) ?? primeiraFrase(d.message) ?? padrao
}

/**
 * A frase de quando o servidor não mandou uma que sirva: o que houve, pelo
 * status, e o que fazer.
 */
export function fraseDoStatus(status: number): string {
  if (status === 401) return "Sessão expirada. Tente novamente."
  if (status === 403) return "Esta conta não tem acesso a isso."
  if (status === 429) return "Muitas tentativas seguidas. Espere um pouco e tente de novo."
  if (status >= 500) return "O servidor falhou agora. Tente de novo em instantes."
  return "Não deu certo agora. Tente de novo."
}

/** O que o Nest, o Express e o class-validator escrevem sozinhos, em inglês. */
const TEXTO_DO_FRAMEWORK: RegExp[] = [
  // O texto do status HTTP, que é o `message` de toda exceção sem frase própria.
  /^(bad request|unauthorized|payment required|forbidden( resource)?|not found|method not allowed|not acceptable|request timeout|conflict|gone|payload too large|request entity too large|unsupported media type|unprocessable entity|too many requests|internal server error|not implemented|bad gateway|service unavailable|gateway timeout|file too large|unexpected field|too many files)\.?$/i,
  /^cannot (get|post|put|patch|delete|head|options) \//i, // a rota que não existe
  /^validation failed\b/i, // os pipes do Nest (ParseIntPipe, ParseUUIDPipe)
  /\b(must|should)\b|an unknown value was passed/i, // o class-validator sem mensagem própria
]

/** Cara de erro interno. */
const CARA_DE_ERRO_INTERNO: RegExp[] = [
  /\n\s*at\s/, // linha de pilha
  /\.(ts|js|mjs|cjs):\d+|node_modules/, // caminho de arquivo
  /\b([A-Z]\w*)?(Error|Exception)\b/, // TypeError, QueryFailedError, ThrottlerException
  /\b(select|insert into|update|delete from)\b.+\b(from|where|set|values)\b/i, // SQL
  /duplicate key|violates|relation "|column "|syntax error at or near|deadlock detected/i, // Postgres
  /\b(ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|EPIPE)\b/, // rede do Node
  /cannot read propert|is not a function|is not defined|\bundefined\b/i, // JavaScript quebrado
  /unexpected token|json at position|json input|not valid json/i, // JSON quebrado
]

function fraseParaGente(frase: string): boolean {
  if (!frase) return false
  if (TEXTO_DO_FRAMEWORK.some((r) => r.test(frase))) return false
  return !CARA_DE_ERRO_INTERNO.some((r) => r.test(frase))
}
